/**
 * Shared, platform-agnostic skill taxonomy helpers used by both the native and
 * web Skills hub. The redesign groups skills into two shelves within a tab -
 * "CV skills" (skills that write/edit a CV) and "Other skills" (letters, posts,
 * interview prep, outreach…): and gives each skill a neutral category icon.
 *
 * Skills v2 (tabbio/v2 catalog) adds first-class fields: `type`, `category`,
 * `country`, `level`, `language`, `recommendedModel`, `contextSources`,
 * `layout`: but legacy rows may lack them (or carry the importer default
 * `type: 'other'` while their `categories[]` still mark them as CV skills), so
 * every helper here prefers the v2 field and falls back to the legacy
 * categories/region/tags heuristics.
 *
 * This file returns semantic keys / i18n key paths only; both platforms resolve
 * those keys through the shared Hugeicons contract.
 */
import type {
  SkillCategory,
  SkillCountry,
  SkillInputField,
  SkillLevel,
  SkillRecommendedModel,
  SkillSummary,
  SkillType,
} from '@tabbio/types';

// Re-exported so hub/detail/run surfaces (and the native counterparts) can
// import the v2 catalog vocabulary alongside the taxonomy helpers.
export type {
  SkillCategory,
  SkillCountry,
  SkillInputField,
  SkillLevel,
  SkillRecommendedModel,
  SkillType,
};

export type SkillIconKey =
  | 'cv'
  | 'letter'
  | 'interview'
  | 'social'
  | 'outreach'
  | 'jobSearch'
  | 'coaching'
  | 'wellbeing'
  | 'generic';

export type SkillCatalogIconKey =
  | SkillIconKey
  | 'relocateToGulf'
  | 'linkedin'
  | 'cvHealthCheck'
  | 'gulfExecutive';

/** Free-form category slug → semantic icon key. Unknown slugs fall through. */
const CATEGORY_ICON: Record<string, SkillIconKey> = {
  'cv-writing': 'cv',
  cv: 'cv',
  resume: 'cv',
  'cover-letter': 'letter',
  letter: 'letter',
  interview: 'interview',
  'social-post': 'social',
  social: 'social',
  outreach: 'outreach',
  networking: 'outreach',
  'job-search': 'jobSearch',
  coaching: 'coaching',
  wellbeing: 'wellbeing',
  // tabbio/v2 catalog categories (other-skills). They reuse the existing
  // semantic keys so the shared Hugeicons map keeps
  // working unchanged: apply → briefcase-like, message → mail,
  // general → sparkle; `interview` is already mapped above.
  apply: 'jobSearch',
  message: 'letter',
  general: 'generic',
};

type SkillLike = Pick<SkillSummary, 'categories'> &
  Partial<Pick<SkillSummary, 'category' | 'type'>>;

export function skillIconKey(skill: SkillLike): SkillIconKey {
  // v2 rows: an explicit cv type or catalog category wins.
  if (skill.type === 'cv') return 'cv';
  const v2Category = normalizeSkillCategory(skill.category);
  if (v2Category) {
    const key = CATEGORY_ICON[v2Category];
    if (key) return key;
  }
  for (const category of skill.categories ?? []) {
    const key = CATEGORY_ICON[category];
    if (key) return key;
  }
  return 'generic';
}

/**
 * A skill belongs under "CV skills" when any of its categories marks it as
 * writing/editing a CV. Everything else (and un-categorised skills) sits under
 * "Other skills".
 */
export function isCvSkill(skill: Pick<SkillSummary, 'categories'>): boolean {
  return (skill.categories ?? []).some(
    (c) => c === 'cv-writing' || c === 'cv' || c === 'resume' || c.includes('cv'),
  );
}

/**
 * Canonical CV/other discriminator. Prefers the v2 `type` field; legacy rows
 * (no `type`, or the importer default `'other'` while `categories[]` still say
 * cv-writing) fall back to the {@link isCvSkill} heuristic so old CV skills
 * keep shelving correctly.
 */
export function skillType(skill: SkillLike): SkillType {
  if (skill.type === 'cv') return 'cv';
  if (isCvSkill(skill)) return 'cv';
  return 'other';
}

/**
 * Split a loaded page of skills into the two reference shelves, preserving the
 * server order within each group.
 */
export function groupSkillsByShelf<T extends SkillLike>(
  skills: T[],
): { cv: T[]; other: T[] } {
  const cv: T[] = [];
  const other: T[] = [];
  for (const skill of skills) {
    (skillType(skill) === 'cv' ? cv : other).push(skill);
  }
  return { cv, other };
}

// ── Country-flag rows ─────────────────────────────────────────────────────────

type SkillFlagLike = SkillLike &
  Partial<
    Pick<
      SkillSummary,
      'skillId' | 'iconUrl' | 'iconKey' | 'country' | 'region'
    >
  >;

/**
 * Canonical "does this row render a country flag?" rule, shared by the icon
 * components and list ordering so they can never disagree: a custom uploaded
 * icon always wins; otherwise an explicit `flag` iconKey, or a CV skill with a
 * country/region, renders the flag. Returns the region string the flag shows
 * (may be empty for an explicit `flag` iconKey), or null when there is no flag.
 */
export function skillFlagRegion(skill: SkillFlagLike): string | null {
  if (skill.iconUrl) return null;
  const region = String(skill.country ?? skill.region ?? '');
  if (skill.iconKey === 'flag') return region;
  return skillType(skill) === 'cv' && region ? region : null;
}

export function skillShowsFlag(skill: SkillFlagLike): boolean {
  return skillFlagRegion(skill) !== null;
}

export type SkillCatalogVisual =
  | { kind: 'image'; uri: string }
  | { kind: 'flag'; region: string }
  | { kind: 'hugeicon'; key: SkillCatalogIconKey };

export function skillCatalogIconOverride(
  skillId: string | null | undefined,
): SkillCatalogIconKey | null {
  const id = skillId?.trim().toLowerCase();
  if (!id) return null;
  if (id === 'relocate-to-gulf-cv') return 'relocateToGulf';
  if (id === 'gulf-executive-cv') return 'gulfExecutive';
  if (id === 'cv-health-check') return 'cvHealthCheck';
  if (id.startsWith('linkedin-') || id.startsWith('li-')) return 'linkedin';
  return null;
}

/**
 * Shared visual precedence for web and native catalog icons: uploaded image,
 * then a market with artwork (every recognised country, and the Gulf, which
 * has its own globe mark), then a per-skill override, then a flag the renderer
 * cannot name (it answers with a neutral globe), then the category glyph.
 *
 * Artwork before override matches the marketing site: the two pan-Gulf CV
 * skills used to reach their plane and globe-search overrides only because the
 * Gulf had no mark of its own. It has one now, so they fly it everywhere.
 */
export function skillCatalogVisual(skill: SkillFlagLike): SkillCatalogVisual {
  if (skill.iconUrl) return { kind: 'image', uri: skill.iconUrl };
  const region = skillFlagRegion(skill);
  if (region !== null && normalizeSkillCountry(region)) {
    return { kind: 'flag', region };
  }
  const override = skillCatalogIconOverride(skill.skillId);
  if (override) return { kind: 'hugeicon', key: override };
  if (region !== null) return { kind: 'flag', region };
  return { kind: 'hugeicon', key: skillIconKey(skill) };
}

// ── Shared explore-list pipeline (native + web render the result as-is) ──────

export type SkillSort = 'top' | 'popular' | 'new' | 'az';

export interface SkillListFilters {
  sort: SkillSort;
  country: SkillCountry | 'all';
  level: SkillLevel;
  language: 'any' | 'en' | 'ar' | 'bi';
  category: SkillCategory | 'all';
  /**
   * "My skills" tab: keep only rows this user created. Matched on
   * `ownerUserId` (authorship of record) and never on `authorUserId`, which is
   * display attribution and can name Tabbio on a row the user does not own.
   */
  createdBy?: string | null;
}

const SKILL_SORT_COMPARATORS: Record<
  SkillSort,
  (a: SkillSummary, b: SkillSummary) => number
> = {
  top: () => 0, // keep server relevance order
  // Runs, not installs: see `skills.usageCount` in the locales.
  popular: (a, b) => b.usageCount - a.usageCount,
  new: (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  az: (a, b) => a.skillId.localeCompare(b.skillId),
};

/**
 * The one list pipeline behind the Skills directory on every platform:
 * client-side filters, then the chosen sort with country-flag skills always
 * pinned above the rest (sort applies within each group; ties keep server
 * order since Array.sort is stable).
 */
export function filterAndSortSkills(
  rows: SkillSummary[],
  filters: SkillListFilters,
): SkillSummary[] {
  const { sort, country, level, language, category, createdBy } = filters;
  let next = rows;
  if (createdBy) {
    next = next.filter((skill) => skill.ownerUserId === createdBy);
  }
  if (country !== 'all') {
    next = next.filter((skill) => skillMatchesCountry(skill, country));
  }
  if (level !== 'all') {
    next = next.filter((skill) => skillMatchesLevel(skill, level));
  }
  if (language !== 'any') {
    next = next.filter((skill) => skillMatchesLanguage(skill, language));
  }
  if (category !== 'all') {
    next = next.filter((skill) => skillMatchesCategory(skill, category));
  }
  const compare = SKILL_SORT_COMPARATORS[sort];
  return [...next].sort(
    (a, b) =>
      Number(skillShowsFlag(b)) - Number(skillShowsFlag(a)) || compare(a, b),
  );
}

// ── v2 enum normalizers (accept v2 codes AND legacy region words / tags) ──────

const COUNTRY_BY_ALIAS: Record<string, SkillCountry> = {
  // v2 codes
  uae: 'uae',
  ksa: 'ksa',
  kwt: 'kwt',
  qat: 'qat',
  omn: 'omn',
  bhr: 'bhr',
  gulf: 'gulf',
  // legacy `region` words (SKILL_REGION_OPTIONS) + common names
  kuwait: 'kwt',
  qatar: 'qat',
  oman: 'omn',
  bahrain: 'bhr',
  gcc: 'gulf',
  'saudi arabia': 'ksa',
};

export function normalizeSkillCountry(
  value: string | null | undefined,
): SkillCountry | null {
  if (!value) return null;
  return COUNTRY_BY_ALIAS[value.trim().toLowerCase()] ?? null;
}

const LEVEL_BY_ALIAS: Record<string, SkillLevel> = {
  // v2 codes
  intern: 'intern',
  fresh: 'fresh',
  mid: 'mid',
  exec: 'exec',
  all: 'all',
  // legacy level tags (SKILL_LEVEL_OPTIONS)
  'fresh-grad': 'fresh',
  'mid-career': 'mid',
  executive: 'exec',
};

export function normalizeSkillLevel(
  value: string | null | undefined,
): SkillLevel | null {
  if (!value) return null;
  return LEVEL_BY_ALIAS[value.trim().toLowerCase()] ?? null;
}

export function normalizeSkillCategory(
  value: string | null | undefined,
): SkillCategory | null {
  const v = value?.trim().toLowerCase();
  return v === 'apply' || v === 'interview' || v === 'message' || v === 'general'
    ? v
    : null;
}

// ── i18n key-path helpers (keys live in en/ar common.ts, `skills.` namespace) ─

/** `skills.countries.<code>`: accepts v2 codes and legacy region words. */
export function countryLabelKey(
  country: string | null | undefined,
): string | null {
  const code = normalizeSkillCountry(country);
  return code ? `skills.countries.${code}` : null;
}

/** `skills.levels.<code>`: accepts v2 codes and legacy level tags. */
export function levelLabelKey(level: string | null | undefined): string | null {
  const code = normalizeSkillLevel(level);
  return code ? `skills.levels.${code}` : null;
}

/** `skills.categories.<code>`: v2 other-skill catalog categories. */
export function categoryLabelKey(
  category: string | null | undefined,
): string | null {
  const code = normalizeSkillCategory(category);
  return code ? `skills.categories.${code}` : null;
}

/** v2 model code → the existing `skills.newFlow.models.<id>` catalog entry. */
const MODEL_LABEL_ID: Record<SkillRecommendedModel, string> = {
  lite: 'gemini-flash-lite',
  flash: 'gemini-flash',
  gpt: 'gpt-4o-mini',
  sonnet: 'claude-sonnet',
  gpro: 'gemini-pro',
  opus: 'claude-opus',
};

export function normalizeSkillModel(
  value: string | null | undefined,
): SkillRecommendedModel | null {
  const v = value?.trim().toLowerCase();
  return v && v in MODEL_LABEL_ID ? (v as SkillRecommendedModel) : null;
}

/** `skills.newFlow.models.<catalog-id>.name` (reuses the builder's catalog). */
export function modelLabelKey(model: string | null | undefined): string | null {
  const code = normalizeSkillModel(model);
  return code ? `skills.newFlow.models.${MODEL_LABEL_ID[code]}.name` : null;
}

/** Models that ship behind the Pro plan (show the Pro pill next to them). */
export function isProModel(model: string | null | undefined): boolean {
  const code = normalizeSkillModel(model);
  return code === 'sonnet' || code === 'gpro' || code === 'opus';
}

/** `skills.contextSources.<source>`: what a v2 skill reads before running. */
export function contextSourceLabelKey(
  source: string | null | undefined,
): string | null {
  const v = source?.trim().toLowerCase();
  return v === 'primary_cv' || v === 'profile' || v === 'memory'
    ? `skills.contextSources.${v}`
    : null;
}

// ── Filter matchers (v2 field first, legacy region/tags fallback) ─────────────

/** Country filter: v2 `country` code first, else legacy `region` word / tags. */
export function skillMatchesCountry(
  skill: Pick<SkillSummary, 'tags'> &
    Partial<Pick<SkillSummary, 'country' | 'region'>>,
  filter: SkillCountry,
): boolean {
  const own = normalizeSkillCountry(skill.country);
  if (own) return own === filter;
  const legacy = normalizeSkillCountry(skill.region);
  if (legacy) return legacy === filter;
  const tagged = (skill.tags ?? []).filter((tag) =>
    Boolean(normalizeSkillCountry(tag)),
  );
  // A skill that declares no country at all is unscoped, so it passes every
  // country filter. Portable ("Build a skill") skills carry no audience, and
  // returning false here would delete them from the catalog the moment any
  // country chip is tapped. A skill that DOES declare a country still fails a
  // different one.
  if (!tagged.length) return true;
  return tagged.some((tag) => normalizeSkillCountry(tag) === filter);
}

/**
 * Level filter: v2 `level` first (`'all'` matches every level), else the
 * legacy carried-as-a-tag heuristic.
 */
export function skillMatchesLevel(
  skill: Pick<SkillSummary, 'tags'> & Partial<Pick<SkillSummary, 'level'>>,
  filter: SkillLevel,
): boolean {
  if (filter === 'all') return true;
  const own = normalizeSkillLevel(skill.level);
  if (own) return own === 'all' || own === filter;
  const tagged = (skill.tags ?? []).filter((tag) =>
    Boolean(normalizeSkillLevel(tag)),
  );
  // Same rule as country: undeclared means unscoped, not excluded.
  if (!tagged.length) return true;
  return tagged.some((tag) => normalizeSkillLevel(tag) === filter);
}

/**
 * Language filter: v2 `language` first (a bilingual skill matches en AND ar),
 * else the legacy `languages[]` array logic.
 */
export function skillMatchesLanguage(
  skill: Pick<SkillSummary, 'languages'> &
    Partial<Pick<SkillSummary, 'language'>>,
  filter: 'en' | 'ar' | 'bi',
): boolean {
  const own = skill.language?.trim().toLowerCase();
  if (own === 'en' || own === 'ar' || own === 'bi') {
    return filter === 'bi' ? own === 'bi' : own === filter || own === 'bi';
  }
  const langs = (skill.languages ?? []).map((l) => l.toLowerCase());
  // `en`/`ar` already treated an empty list as unscoped; `bi` did not. Align
  // it so all three facets follow the same rule.
  if (langs.length === 0) return true;
  if (filter === 'bi') return langs.includes('en') && langs.includes('ar');
  return langs.includes(filter);
}

/** Category chips: legacy rows (no `category`) only show under "All". */
export function skillMatchesCategory(
  skill: Partial<Pick<SkillSummary, 'category'>>,
  filter: SkillCategory | 'all',
): boolean {
  if (filter === 'all') return true;
  return normalizeSkillCategory(skill.category) === filter;
}

// ── Declarative run-screen inputs (tabbio/v2 `inputs[]`) ──────────────────────

/**
 * Parses a detail's `inputsSchema` into declarative run-screen fields. Returns
 * the field list only when the schema is a well-formed tabbio/v2 array: any
 * legacy object schema (or malformed entry) returns null so the run flow keeps
 * its current type-aware inputs exactly.
 */
/** True once every required declared field has a non-empty value. */
export function declaredInputsComplete(
  fields: SkillInputField[] | null | undefined,
  values: Record<string, string>,
): boolean {
  if (!fields) return true;
  return fields.every(
    (field) => !field.required || (values[field.id] ?? '').trim().length > 0,
  );
}

/** Run `inputs` payload from declared field values (trimmed, empties dropped). */
export function declaredInputsPayload(
  fields: SkillInputField[],
  values: Record<string, string>,
): Record<string, unknown> {
  const inputs: Record<string, unknown> = {};
  for (const field of fields) {
    const value = (values[field.id] ?? '').trim();
    if (value) inputs[field.id] = value;
  }
  return inputs;
}

export function skillInputFields(
  inputsSchema: unknown,
): SkillInputField[] | null {
  if (!Array.isArray(inputsSchema) || inputsSchema.length === 0) return null;
  const fields: SkillInputField[] = [];
  for (const entry of inputsSchema) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === 'string' ? e.id.trim() : '';
    const label = typeof e.label === 'string' ? e.label.trim() : '';
    const type =
      e.type === 'text' || e.type === 'textarea' || e.type === 'select'
        ? e.type
        : null;
    if (!id || !label || !type) return null;
    const options = Array.isArray(e.options)
      ? e.options.filter(
          (o): o is string => typeof o === 'string' && o.trim().length > 0,
        )
      : undefined;
    fields.push({
      id,
      label,
      type,
      required: e.required === true,
      placeholder:
        typeof e.placeholder === 'string' && e.placeholder.trim()
          ? e.placeholder
          : undefined,
      options: options && options.length > 0 ? options : undefined,
    });
  }
  return fields;
}
